import { useEffect, useRef } from 'react';
import { trackAppPageView } from '../../analytics/appInsights';
import { removeSensitiveParamsFromSearch } from '../../utils/app/queryParams';

import { useAppData } from '../../context';
import { APP_PHASES } from '../../hooks/useAppInitialization/reducer';
import {
  fireViewEndCustomEvent,
  fireViewStartCustomEvent,
} from '../../analytics/analytics';
import loadDataLayer from '../../analytics/loadDataLayer';
import { findModelName } from '../../utils/helpers/functions.helper';

const SATELLITE_POLL_INTERVAL_MS = 300;
const SATELLITE_WAIT_TIMEOUT_MS = 10000;

function getSanitizedPageUri() {
  const safeSearch = removeSensitiveParamsFromSearch(window.location.search);

  // Rolex currently has no meaningful hash routes, so omit the fragment from
  // telemetry to prevent accidental disclosure of fragment data.
  return `${window.location.origin}${window.location.pathname}${safeSearch}`;
}

function getProductAnalyticsData(businessData) {
  const modelName = findModelName(businessData)?.[0]?.Translation || 'ROLEX';

  const familyName =
    businessData?.[0]?.TranslationsForEnglish?.find((item) =>
      item?.Type?.includes('FAMILY_NAME'),
    )?.Translation || 'ROLEX';

  return {
    modelName: modelName.toLowerCase(),
    familyName: familyName.toLowerCase(),
  };
}

function createViewKey({ phase, watchData, status }) {
  if (phase === APP_PHASES.PRODUCT) {
    const productIdentifier =
      watchData?.Configuration?.Rmc ||
      watchData?.Configuration?.Model ||
      watchData?.SerialNumber ||
      'unknown';

    return `${phase}:${productIdentifier}`;
  }

  return `${phase}:${status ?? ''}`;
}

function scheduleViewEnd() {
  if (window.__satelliteLoaded) {
    fireViewEndCustomEvent();
    return () => {};
  }

  const startedAt = Date.now();

  const intervalId = window.setInterval(() => {
    const hasTimedOut = Date.now() - startedAt >= SATELLITE_WAIT_TIMEOUT_MS;

    if (window.__satelliteLoaded) {
      window.clearInterval(intervalId);
      fireViewEndCustomEvent();
      return;
    }

    if (hasTimedOut) {
      window.clearInterval(intervalId);
    }
  }, SATELLITE_POLL_INTERVAL_MS);

  return () => window.clearInterval(intervalId);
}

export function useResolvedViewTracking() {
  const { phase, watchData, businessData, status } = useAppData();
  const lastTrackedViewRef = useRef('');

  useEffect(() => {
    const isTrackableView =
      phase === APP_PHASES.PRODUCT ||
      phase === APP_PHASES.ASSOCIATION ||
      phase === APP_PHASES.ERROR;

    if (!isTrackableView) {
      return undefined;
    }

    const viewKey = createViewKey({ phase, watchData, status });

    if (lastTrackedViewRef.current === viewKey) {
      return undefined;
    }

    lastTrackedViewRef.current = viewKey;

    const isProductView = phase === APP_PHASES.PRODUCT;
    const isAssociationView = phase === APP_PHASES.ASSOCIATION;

    const { modelName, familyName } = isProductView
      ? getProductAnalyticsData(businessData)
      : { modelName: '', familyName: '' };

    fireViewStartCustomEvent({ detail: '' });

    loadDataLayer(
      modelName,
      familyName,
      isProductView ? watchData : null,
      isAssociationView ? 200 : status,
    );

    const pageName = window.digitalDataLayer?.page?.pageInfo?.pageName || phase;

    trackAppPageView({
      name: pageName,
      uri: getSanitizedPageUri(),
      properties: {
        phase,
        status: status ?? '',
      },
    });

    return scheduleViewEnd();
  }, [phase, watchData, businessData, status]);
}
